import type {
  FlowerAdBreakListenerLike,
  FlowerAdInfo,
  FlowerAdSkipListenerLike,
  FlowerAdViewConstructor,
  FlowerAdViewInstance,
  FlowerAdsListener,
} from '@apm/bloom-receiver-sdk';
import type { OnairAdEvent } from './cspm.js';
import { cspmTagFor } from './cspm.js';

/**
 * v38 · ONAIR CSPM 의 **Flower 배선** (`docs/77` §5.2 · 절대 규칙 101).
 *
 * `use-onair-player.ts` 가 hls.js 를 `attachMedia` 한 **뒤에** 부른다. 하는 일은 셋 — 번들 적재(지금 적재기 · 페이지당 한 번) ·
 * 숨은 `FlowerAdView` · `changeChannelUrl(…, { getPlayer: () => hls })` 로 바꾼 주소를 `loadSource`. hls.js·`<video>` 는 앱의
 * 것이고 Flower 는 그것을 감쌀 뿐이다 — **셋째 `<video>` 가 없다.** DOM·hls.js 는 `deps` 로만 닿는다(이 파일의 시험이 node 에서 돈다).
 *
 * ── ★ 실패는 원 주소다 (D8) ─────────────────────────────────
 *
 * 번들 적재 실패 · `FlowerAdView` 생성이나 `changeChannelUrl` 의 예외면 **원 주소로 `loadSource`** 한다 — 브레이크에는 서버가
 * 깐 슬레이트가 흐르고 채널은 선다. 이유는 `status` 로 남기고 콘솔에 `warn` 한다(조용한 강등 금지 · 규칙 93 의 모양).
 * 광고 오류·no fill·늦은 합류는 **Flower 가** 그 브레이크를 슬레이트에 맡기는 것이고(`onAdBreakSkipped` · `onError`) 재생 주소는
 * 그대로다 — 이 파일은 그 이유를 화면 상태로 옮길 뿐이다.
 *
 * ── `ignoreSkip()` 을 부르지 않는다 ─────────────────────────
 *
 * 그것은 Flower 의 스킵 **무장 자체**를 끈다(`docs/77` §5.2). 숨은 자리의 Flower 스킵 버튼은 눌릴 수 없고, 스킵은 앱이 그린
 * 버튼이 `skipCurrentAd()` 로 부른다(bloom.15 · `docs/74` §4A).
 */

export type CspmStatus =
  | { state: 'loading' }
  | { state: 'on'; src: string }
  | { state: 'fallback'; reason: CspmFallbackReason; detail: string };

export type CspmFallbackReason = 'bundle-load-failed' | 'ad-view-failed' | 'change-channel-url-threw';

export interface CspmSessionDeps {
  /** `loadFlowerSdk` — 페이지당 한 번(성공이든 실패든 같은 약속을 준다) */
  load: () => Promise<{ FlowerAdView: FlowerAdViewConstructor }>;
  /** 화면 밖의 숨은 자리를 만든다 · `remove` 가 뗀다 */
  mountAdView: () => { container: HTMLElement; remove: () => void };
  /** 앱의 hls.js — Flower 가 `getPlayer` 로 받는다 */
  player: unknown;
  /** 앱의 hls.js 의 `loadSource` — 성공이면 바뀐 주소, 실패면 원 주소 */
  loadSource: (url: string) => void;
  /** 앱의 `<video>` 의 `currentTime` — 남은 시간·스킵 초의 축(규칙 57 의 재생 축) */
  mediaSec: () => number;
  dispatch: (event: OnairAdEvent) => void;
  onStatus: (status: CspmStatus) => void;
  /** Flower 의 `onError` — 재생은 그대로다(Flower 가 슬레이트에 맡긴다) · 디버그에만 */
  onFlowerError?: (detail: string) => void;
  warn?: (message: string) => void;
}

export interface CspmSession {
  /** 앱이 그린 스킵 버튼 — 받아들여지지 않으면 `false`(세션이 서기 전 · Flower 가 거절) */
  skipCurrentAd(): boolean;
  /** 떠날 때 — 리스너를 떼고 `adsManager.stop()` · 숨은 자리를 뗀다. 적재 중이면 끝난 뒤 아무것도 하지 않는다 */
  stop(): void;
}

export function startCspmSession(
  input: { streamUrl: string; adTagUrl: string; channelId: string; deviceId: string },
  deps: CspmSessionDeps,
): CspmSession {
  const warn = deps.warn ?? ((m: string) => console.warn(m));
  let stopped = false;
  let view: FlowerAdViewInstance | null = null;
  let mount: { remove: () => void } | null = null;

  const listener: FlowerAdsListener = {
    onPlay: () => deps.dispatch({ type: 'play', mediaSec: deps.mediaSec() }),
    onAdPlay: (info: FlowerAdInfo) => deps.dispatch({ type: 'adPlay', info, mediaSec: deps.mediaSec() }),
    onCompleted: () => deps.dispatch({ type: 'completed' }),
    onAdBreakSkipped: (reason: number) => deps.dispatch({ type: 'breakSkipped', reason }),
    onError: (error: unknown) => deps.onFlowerError?.(describe(error)),
  };
  const skipListener: FlowerAdSkipListenerLike = {
    onAdSkippable: (info) => deps.dispatch({ type: 'skippable', info }),
    onAdSkipExpired: (info) => deps.dispatch({ type: 'skipExpired', info }),
  };
  // bloom.24 — 브레이크가 시작될 때 남은 길이(한가운데 합류면 남은 만큼 · `docs/74` §4F)
  const breakListener: FlowerAdBreakListenerLike = {
    onAdBreakStarted: (info) => deps.dispatch({ type: 'breakStarted', remainingMs: info.adBreakDuration }),
  };

  const fallback = (reason: CspmFallbackReason, cause: unknown) => {
    const detail = describe(cause);
    warn(`[onair/cspm] Flower 를 쓰지 못해 원 재생목록으로 잇는다(브레이크는 슬레이트) — ${reason}: ${detail}`);
    deps.onStatus({ state: 'fallback', reason, detail });
    deps.loadSource(input.streamUrl);
  };

  const release = () => {
    if (view !== null) {
      const manager = view.adsManager;
      view = null;
      try {
        manager.removeListener(listener);
        manager.removeSkipListener(skipListener);
        manager.removeAdBreakListener(breakListener);
        manager.stop();
      } catch (cause) {
        warn(`[onair/cspm] Flower 정리가 던졌다: ${describe(cause)}`);
      }
    }
    mount?.remove();
    mount = null;
  };

  deps.onStatus({ state: 'loading' });
  void deps.load().then(
    ({ FlowerAdView }) => {
      // 적재 동안 떠났다 — 재생도 정리도 화면의 몫이 이미 끝났다
      if (stopped) return;
      try {
        const m = deps.mountAdView();
        mount = m;
        view = new FlowerAdView(m.container);
        view.adsManager.addListener(listener);
        view.adsManager.addSkipListener(skipListener);
        view.adsManager.addAdBreakListener(breakListener);
      } catch (cause) {
        release();
        fallback('ad-view-failed', cause);
        return;
      }
      let src: string;
      try {
        src = view.adsManager.changeChannelUrl(
          input.streamUrl,
          // ★ 기기 id 는 **태그 안에** — 스킵 뒤의 재요청에는 Flower 가 `extraParams` 를 싣지 않는다(§5.3)
          cspmTagFor(input.adTagUrl, input.deviceId),
          input.channelId,
          // `ch`·`lang` 은 서버가 태그에 실었고 `dev` 는 위에서 붙였다 — 여기 싣으면 Flower 의 값이 이긴다(규칙 97)
          new Map(),
          { getPlayer: () => deps.player },
          new Map(),
          new Map(),
        );
      } catch (cause) {
        release();
        fallback('change-channel-url-threw', cause);
        return;
      }
      deps.onStatus({ state: 'on', src });
      deps.loadSource(src);
    },
    (cause: unknown) => {
      if (stopped) return;
      fallback('bundle-load-failed', cause);
    },
  );

  return {
    skipCurrentAd() {
      if (view === null) return false;
      try {
        return view.adsManager.skipCurrentAd();
      } catch (cause) {
        warn(`[onair/cspm] skipCurrentAd 가 던졌다: ${describe(cause)}`);
        return false;
      }
    },
    stop() {
      stopped = true;
      release();
    },
  };
}

function describe(cause: unknown): string {
  if (cause instanceof Error) {
    const inner = cause.cause === undefined ? '' : ` (${describe(cause.cause)})`;
    return `${cause.name}: ${cause.message}${inner}`;
  }
  if (typeof cause === 'object' && cause !== null && 'message' in cause) return String(cause.message);
  return String(cause);
}
